import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { StudiesLayout } from "@/components/layouts";
import { Toolbar, ToolbarItem } from "@/components/ui/toolbar";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useSegment } from "@/hooks/use-segment";
import { AssetUploadDropzone, useUploadsStore } from "@/components/ui/drag-drop-upload";
import { useStudyNavigation } from "@/features/studies/hooks/use-study-navigation";
import { useUpdateRoute } from "@/features/studies/hooks/use-update-route";
import { useGetAssets } from "@/api/studio/assets/queries";

/* Prop Types */
interface StudyAssetSelectRouteProps {
  projectId: string;
  studyId: string;
}

/*
 * StudyAssetSelectRoute component
 * Here the user stages the assets they want to upload to their study
 */
export const StudyAssetSelectRoute = ({ projectId, studyId }: StudyAssetSelectRouteProps) => {
  // Update the route with the current step
  useUpdateRoute(projectId, studyId, "study-select");

  /* Hooks */
  const { segmentPageEvent } = useSegment();
  const { navigateToNext } = useStudyNavigation(projectId, studyId, "study-select");

  /* Stores */
  const { reset } = useUploadsStore((state) => state.uploadActions);

  /* Queries */
  const { data: assetsData } = useGetAssets(projectId, studyId);

  /* Derived state */
  const canProceed = !!assetsData?.assets?.length;

  /* Side Effects */
  useEffect(() => {
    // Reset the uploads store when the component mounts as we are starting a new upload process
    reset();
  }, []);

  /* Handlers */
  const handleUpload = () => {
    // Segment event for moving to the next step
    segmentPageEvent("Study Assets Submitted For AOI Review", {
      projectId,
      studyId,
      studyType: "asset",
    });

    navigateToNext();
  };

  /* Render */
  return (
    <StudiesLayout projectId={projectId} studyId={studyId} currentStep="study-select">
      {/* Primary Toolbar */}
      <Toolbar>
        <ToolbarItem>
          <h1 className="font-semibold">Upload assets</h1>
        </ToolbarItem>
        <ToolbarItem>
          <Button variant="primary" disabled={!canProceed} onClick={handleUpload}>
            Next: review assets <ChevronRightIcon className="size-5" />
          </Button>
        </ToolbarItem>
      </Toolbar>

      {/* Secondary Toolbar */}
      <Toolbar>
        <ToolbarItem>
          <div>
            <p className="text-base-content/70">Supported file types: JPG, JPEG and PNG. Image dimensions cannot exceed 4096 x 4096px.</p>
          </div>
        </ToolbarItem>
      </Toolbar>

      {/* Drag & Drop */}
      <AssetUploadDropzone
        projectId={projectId}
        studyId={studyId}
        dragDropText="or upload assets from your computer"
        dragDropVideoUrl="/videos/upload-animation.webm"
        segmentTrackName="Study"
        segmentTrackMediaType="Asset"
        additionalSegmentProperties={{ studyId, projectId, studyType: "asset" }}
      />
    </StudiesLayout>
  );
};
